import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Rating,
  Checkbox,
  FormControlLabel,
  Paper,
  TextField,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import LocationOn from "../../icon/Loaction.svg";
import GroupIcon from "../../icon/Group.svg";
import NavigationBar from "../../Layout/Header/header";
import Footer from "../../Layout/Footer/footer";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import img from "../../images/agencyportfolioimg.svg";
import { API_CONFIG } from "../../api/apiUrl";
import { AuthContext } from "../../context/authcontext";
import toast, { Toaster } from "react-hot-toast";
import type {
  Contractor,
  PortfolioItem,
  PricingPlan,
  RateCardItem,
  ApiResponse,
  SystemConfig
} from "../../types/ContractorsProfile";

const ContractorsProfile = () => {
  const [selectedTab, setSelectedTab] = useState<string>("Pricing Plans");
  const [contractor, setContractor] = useState<Contractor | null>(null);
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [expandedPlanIndex, setExpandedPlanIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [marginConfig, setMarginConfig] = useState<SystemConfig | null>(null);
  const [isMarginApplied, setIsMarginApplied] = useState<boolean>(false);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState<number | null>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = localStorage.getItem("id_token");

  const authContext = useContext(AuthContext);
  if (!authContext) throw new Error("ContractorsProfile must be used within an AuthProvider");
  const { user, selectedPlotId } = authContext;
  const userId = user?.id;
  const userLogin = user?.login || "unknown";
  const userFirstName = user?.firstName || "User";

  const tabs: string[] = ["Services We Provide", "Pricing Plans"];

  const fetchContractor = async () => {
    try {
      const response = await axios.get<Contractor>(
        API_CONFIG.COMPANY_PROFILE_BY_ID(id!),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setContractor(response.data);
    } catch (error) {
      console.error("Error fetching contractor:", error);
      toast.error("Failed to fetch contractor data");
    }
  };

  const fetchPricingPlans = async () => {
    try {
      const response = await axios.get<ApiResponse[]>(
        API_CONFIG.COMPANY_PROFILE_RATES_BY_ID(id!),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const plans: PricingPlan[] = [];
      const processedTypeIds = new Set<number>();


      response.data.forEach((item) => {
        if (item.rateCardTypes && item.companyRates) {
          const typeId = item.rateCardTypes.id;
          processedTypeIds.add(typeId);

          plans.push({
            name: item.rateCardTypes.name,
            price: item.companyRates.price || item.rateCardTypes.defaultPrice,
            isAvailable: true,
            companyRateId: item.companyRates.id,
            rateCardTypeId: typeId,
            features:
              item.rateCardItems?.map((feature) => ({
                id: feature.rateCardItems.id,
                name: feature.rateCardItems.name,
                isEnabled: true,
              })) || [],
            editing: false,
            showAllFeatures: false,
          });
        }
      });

      response.data.forEach((item) => {
        if (item.rateCardNotMapped && item.rateCardNotMapped.length > 0) {
          item.rateCardNotMapped.forEach((unmappedItem) => {
            const typeId = unmappedItem.rateCardTypes.id;
            if (processedTypeIds.has(typeId)) return;

            processedTypeIds.add(typeId);

            plans.push({
              name: unmappedItem.rateCardTypes.name,
              price: unmappedItem.rateCardTypes.defaultPrice,
              isAvailable: true,
              companyRateId: 0,
              rateCardTypeId: typeId,
              features: unmappedItem.rateCardItems.map((feature: RateCardItem) => ({
                id: feature.rateCardItems.id,
                name: feature.rateCardItems.name,
                isEnabled: true,
              })),
              editing: false,
              showAllFeatures: false,
            });
          });
        }
      });

      setPricingPlans(plans);
    } catch (error) {
      console.error("Error fetching pricing plans:", error);
      toast.error("Failed to fetch pricing plans");
    }
  };

  const fetchMarginConfig = async () => {
    try {
      const response = await axios.get<SystemConfig[]>(
        `${API_CONFIG.SYSTEM_CONFIGS}?keyName.equals=MARGIN&page=0&size=20`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data && response.data.length > 0) {
        setMarginConfig(response.data[0]);
      }
    } catch (error) {
      console.error("Error fetching margin config:", error);
    }
  };

  const postPlotRateCard = async (
    plan: PricingPlan,
    userId: number | undefined,
    selectedPlotId: number | null | undefined,
    adjustedPrice: number
  ) => {
    try {
      await axios.post(
        API_CONFIG.PLOT_RATE_CARDS,
        {
          remarks: "Negotiated price for selected plan",
          negotiatedPrice: adjustedPrice,
          approvalRemarks: "",
          user: { id: userId },
          updatedBy: { id: userId },
          plot: { id: selectedPlotId },
          companyRates: { id: plan.companyRateId },
          isApproved: true,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Plan selected successfully!");
    } catch (error) {
      console.error("Error posting plot rate card:", error);
      toast.error("Failed to select plan");
    }
  };

  const handleEditPrice = (index: number) => {
    const updatedPlans = [...pricingPlans];
    updatedPlans[index].editing = true;
    setPricingPlans(updatedPlans);
  };

  const handleSavePrice = async (index: number) => {
    if (!contractor || !userId) {
      toast.error("Missing contractor or user data");
      return;
    }

    const plan = pricingPlans[index];
    try {
      const payload = {
        rateCardTypes: { id: plan.rateCardTypeId },
        companyRates: {
          price: plan.price,
          companyProfile: { id: contractor.id },
          user: { id: userId, login: userLogin, firstName: userFirstName },
          rateCardTypes: { id: plan.rateCardTypeId },
        },
        rateCardItems: plan.features
          .filter((feature) => feature.isEnabled)
          .map((feature) => ({
            rateCardItems: { id: feature.id },
          })),
      };

      await axios.post(API_CONFIG.COMPANY_RATES, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const updatedPlans = [...pricingPlans];
      updatedPlans[index].editing = false;
      setPricingPlans(updatedPlans);
      toast.success(`Price for ${plan.name} updated successfully!`);

      await fetchPricingPlans();
    } catch (error) {
      console.error("Error saving price:", error);
      toast.error("Failed to update price. Please try again.");
    }
  };

  const handlePriceChange = (index: number, newPrice: string) => {
    const updatedPlans = [...pricingPlans];
    updatedPlans[index].price = Number(newPrice) || 0;
    setPricingPlans(updatedPlans);
  };

  const handleFeatureChange = (planIndex: number, featureIndex: number, checked: boolean) => {
    const updatedPlans = [...pricingPlans];
    const plan = updatedPlans[planIndex];
    if (plan.features && featureIndex >= 0 && featureIndex < plan.features.length) {
      plan.features[featureIndex].isEnabled = checked;
      setPricingPlans(updatedPlans);
    }
  };

  const handleToggleFeatures = (planIndex: number) => {
    setExpandedPlanIndex(expandedPlanIndex === planIndex ? null : planIndex);
  };

  // const handlePlanSelect = (plan: PricingPlan, index: number) => {
  //   setSelectedPlanIndex(index);
  // };

  // const handlePlanClick = async (plan: PricingPlan) => {
  //   if (!contractor) {
  //     toast.error("Contractor data not available");
  //     return;
  //   }
  //   const adjustedPrice = getAdjustedPrice(plan.price);
  //   await postPlotRateCard(plan, userId, selectedPlotId, adjustedPrice);
  //   if (selectedPlotId) {
  //     navigate(`/milestone/${id}?plotId=${selectedPlotId}`);
  //   } else {
  //     navigate(`/milestone/${id}`);
  //   }
  // };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchContractor(),
        fetchPricingPlans(),
        fetchMarginConfig(),
      ]);
      setLoading(false);
    };
    fetchData();
  }, [id]);

  const handleTabClick = (tab: string) => {
    setSelectedTab(tab);
  };

  // const handleCheckboxChange = (event: any) => {
  //   setIsMarginApplied(event.target.checked);
  // };

  const getAdjustedPrice = (price: number) => {
    if (marginConfig && isMarginApplied) {
      if (marginConfig.isPercent) {
        return Math.floor(price * (1 + marginConfig.valueNumers / 100));
      } else {
        return Math.floor(price + marginConfig.valueNumers);
      }
    }
    return Math.floor(price);
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner"></div>
        <p>Loading, please wait...</p>
      </div>
    );
  }

  if (!contractor) {
    return (
      <Typography align="center" sx={{ p: 2 }}>
        No contractor data found
      </Typography>
    );
  }

  return (
    <>
      <NavigationBar />
      <Box sx={{ maxWidth: "2000px", margin: "0 auto" }}>
        <Box
          sx={{
            py: { xs: 2, sm: 3, md: 3, lg: 3, xl: 3 },
            px: { xs: 2, sm: 5, md: 4, lg: 4, xl: 6 },
          }}
        >
          <Grid container spacing={0}>
            <Grid item xs={12} md={12} lg={4}>
              <Box>
                <Typography sx={styles.agencyName}>{contractor.name}</Typography>
                <Typography sx={styles.agencyDescription}>
                  {contractor.description}
                </Typography>
                <Typography sx={styles.specialtyText}>
                  {contractor.speciality}
                </Typography>
                <Box sx={styles.section}>
                  <img src={LocationOn} alt="" />
                  <Typography sx={styles.locationText}>
                    {contractor.address}, {contractor.cities?.name}
                  </Typography>
                </Box>
                <Box sx={styles.section}>
                  <img src={GroupIcon} alt="" />
                  <Typography sx={styles.locationText}>
                    {contractor.projects} Projects
                  </Typography>
                </Box>
                <Box sx={styles.section}>
                  <img src={GroupIcon} alt="" />
                  <Typography sx={styles.locationText}>
                    {contractor.experience} Years of Experience
                  </Typography>
                </Box>
                <Box sx={styles.section}>
                  <Rating
                    value={contractor.rating}
                    readOnly
                    precision={0.1}
                    sx={{
                      color: "black",
                      "& .MuiRating-icon": {
                        fontSize: { xs: "16px", sm: "20px", md: "20px" },
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={12} lg={8}>
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: "5px", sm: "16px", md: "30px" },
                  marginBottom: "20px",
                }}
              >
                {tabs.map((tab) => (
                  <Button
                    key={tab}
                    onClick={() => handleTabClick(tab)}
                    sx={styles.tabButton(selectedTab, tab)}
                  >
                    {tab}
                  </Button>
                ))}
              </Box>

              <Box>
                {selectedTab === "Services We Provide" && (
                  <Box>
                    <Grid container spacing={2} maxWidth={600}>
                      {services.map((service, index) => (
                        <Grid item xs={6} sm={4} md={3} lg={4} key={index}>
                          <Card
                            elevation={0}
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "34px",
                              border: "1px solid #161717",
                              borderRadius: "25px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: {
                                  xs: "15px",
                                  sm: "16px",
                                  md: "16px",
                                  lg: "17px",
                                  xl: "19px",
                                },
                                fontWeight: 500,
                                padding: "0 10px",
                              }}
                            >
                              {service}
                            </Typography>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {selectedTab === "Pricing Plans" && (
                  <Grid container spacing={2}>
                    {pricingPlans.map((plan, planIndex) => (
                      <Grid item xs={12} sm={6} md={4} lg={4} key={planIndex}>
                        <Card
                          elevation={0}
                          sx={{
                            border: "0.6px solid #161717",
                            borderRadius: "10px",
                            minHeight: "400px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        // onClick={() => handlePlanSelect(plan, planIndex)}
                        >
                          <CardContent>
                            <Typography sx={styles.pricingTitle}>
                              {plan.name}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                marginTop: "10px",
                              }}
                            >
                              {plan.editing ? (
                                <>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 2,
                                      gap: 1,
                                    }}
                                  >
                                    <TextField
                                      value={plan.price || ""}
                                      onChange={(e) =>
                                        handlePriceChange(planIndex, e.target.value)
                                      }
                                      variant="outlined"
                                      size="small"
                                      type="number"
                                      sx={{
                                        width: "80px",
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "12px",
                                          bgcolor: "#f5f5f5",
                                          "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#89652A",
                                          },
                                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#89652A",
                                            borderWidth: "2px",
                                          },
                                        },
                                      }}
                                    />
                                    <Button
                                      variant="contained"
                                      onClick={() => handleSavePrice(planIndex)}
                                      sx={{
                                        backgroundColor: "#89652A",
                                        color: "white",
                                        borderRadius: "12px",
                                        textTransform: "none",
                                      }}
                                    >
                                      Save
                                    </Button>
                                  </Box>
                                  <Box sx={{ width: "100%", mt: 1 }}>
                                    {plan.features.map((feature, featureIndex) => (
                                      <FormControlLabel
                                        key={featureIndex}
                                        control={
                                          <Checkbox
                                            checked={feature.isEnabled}
                                            onChange={(e) =>
                                              handleFeatureChange(
                                                planIndex,
                                                featureIndex,
                                                e.target.checked
                                              )
                                            }
                                            sx={{
                                              color: "#89652A",
                                              "&.Mui-checked": { color: "#89652A" },
                                            }}
                                          />
                                        }
                                        label={feature.name}
                                        sx={{
                                          display: "flex",
                                          "& .MuiFormControlLabel-label": {
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            whiteSpace: "normal",
                                            overflow: "visible",
                                            textOverflow: "unset",
                                          },
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </>
                              ) : (
                                <Box
                                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                >
                                  <Typography sx={styles.pricingPrice}>
                                    ₹{getAdjustedPrice(plan.price)}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditPrice(planIndex)}
                                    sx={{
                                      color: "#89652A",
                                      "&:hover": {
                                        backgroundColor: "rgba(137, 101, 42, 0.1)",
                                      },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                            <Typography sx={styles.pricingSubtitle}>
                              Per Square Feet
                            </Typography>

                            {!plan.editing && (
                              <Box>
                                {plan.features.length > 0 ? (
                                  plan.features
                                    .filter((feature) => feature.isEnabled)
                                    .slice(0, expandedPlanIndex === planIndex ? plan.features.length : 4)
                                    .map((feature, featureIndex) => (
                                      <Typography
                                        key={featureIndex}
                                        sx={styles.featureText}
                                      >
                                        <span style={styles.featureIcon}>
                                          <CheckCircleIcon />
                                        </span>
                                        {feature.name}
                                      </Typography>
                                    ))
                                ) : (
                                  <Typography sx={{ ...styles.featureText, textAlign: 'center', width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    No features available
                                  </Typography>
                                )}
                                {plan.features.length > 4 && (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                    <Button
                                      onClick={() => handleToggleFeatures(planIndex)}
                                      sx={{
                                        textTransform: 'none',
                                        color: '#89652A',
                                        fontSize: '14px',
                                        mt: 1,
                                        mx: 'auto',
                                        display: 'block',
                                      }}
                                    >
                                      {expandedPlanIndex === planIndex ? 'View Less' : 'View More'}
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                        {/* <Button
                  
                          size="small"
                          onClick={() => {
                            handlePlanSelect(plan, planIndex);
                            handlePlanClick(plan);
                          }}
                          sx={{
                            mt: 1,
                            backgroundColor:
                              selectedPlanIndex === planIndex ? "#89652A" : "#99700c",
                            color: "white",
                            borderRadius: "12px",
                            textTransform: "none",
                            margin: "auto",
                            width: "100%",
                            padding: "10px",
                            maxWidth: "120px",
                            display: "block",
                            "&:hover": {
                              backgroundColor: "#89652A",
                            },
                          }}
                        >
                          Select Plan
                        </Button> */}
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Grid>
          </Grid>
          {/* <Box sx={{ mt: 4, mb: 2 }}>
            {marginConfig && (
              <Paper
                elevation={0}
                sx={{
                  maxWidth: "300px",
                  mx: "auto",
                  p: 1,
                  borderRadius: "10px",
                  border: "1px solid #89652A",
                  backgroundColor: isMarginApplied ? "#89652A" : "#fff",
                  color: isMarginApplied ? "#fff" : "#000",
                  transition: "all 0.3s ease",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isMarginApplied}
                      onChange={handleCheckboxChange}
                      sx={{
                        color: isMarginApplied ? "#fff" : "#89652A",
                        "&.Mui-checked": { color: "#fff" },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: { xs: "16px", sm: "18px" },
                        fontWeight: 500,
                        whiteSpace: "normal",
                        overflow: "visible",
                        textOverflow: "unset",
                      }}
                    >
                      Add {marginConfig.valueNumers}
                      {marginConfig.isPercent ? "%" : ""} margin to Pricing
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              </Paper>
            )}
            <Typography
              sx={{
                my: { xs: 2, md: 4 },
                fontSize: { xs: "30px", md: "50px" },
                textTransform: "capitalize",
                fontWeight: 600,
                textAlign: "center",
                whiteSpace: "normal",
                overflow: "visible",
                textOverflow: "unset",
              }}
            >
              Portfolio
            </Typography>
            <Box
              sx={{
                border: "1px solid #E2DFDF",
                padding: { xs: "15px", sm: "16px", md: "50px" },
                borderRadius: "10px",
              }}
            >
              <Grid container spacing={{ xs: 2, md: 2 }}>
                {Portfolio.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box
                      sx={{
                        border: "1px solid #E2DFDF",
                        borderRadius: "10px",
                        padding: "20px",
                      }}
                    >
                      <img
                        src={item.image}

                        alt="Portfolio Item"
                        style={{ width: "100%", borderRadius: "7px" }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: {
                              xs: "15px",
                              sm: "15px",
                              md: "15px",
                              lg: "15px",
                              xl: "15px",
                            },
                            fontWeight: 400,
                            my: 2,
                            textAlign: "center",
                            whiteSpace: "normal", 
                            overflow: "visible",
                            textOverflow: "unset",
                          }}
                        >
                          {item.message}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box> */}
        </Box>
        <Footer />
      </Box>
    </>
  );
};

export default ContractorsProfile;

const styles = {
  agencyName: {
    textTransform: "capitalize",
    fontSize: { xs: "31px", sm: "35px", md: "35px", lg: "35px", xl: "35px" },
    fontWeight: 600,
    mb: 1,
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "unset",
  },
  agencyDescription: {
    textTransform: "capitalize",
    fontSize: { xs: "15px", sm: "18px", md: "18px", lg: "17px", xl: "19px" },
    fontWeight: 400,
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "unset",
  },
  specialtyText: {
    textTransform: "capitalize",
    mt: 2,
    fontWeight: 400,
    fontSize: { xs: "14px", sm: "14px", md: "14px", lg: "16px", xl: "15px" },
    mb: 1,
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "unset",
  },
  locationText: {
    textTransform: "capitalize",
    fontWeight: 400,
    fontSize: { xs: "14px", sm: "14px", md: "14px", lg: "15px", xl: "15px" },
    my: 0.3,
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "unset",
  },
  section: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 0.7,
  },
  tabButton: (selectedTab: string, tab: string) => ({
    textTransform: "none",
    fontWeight: 500,
    fontSize: { xs: "15px", sm: "17px", md: "17px", lg: "17px", xl: "18px" },
    backgroundColor: selectedTab === tab ? "#89652A" : "transparent",
    color: selectedTab === tab ? "#fff" : "black",
    border: selectedTab === tab ? "1px solid #89652A" : "1px solid black",
    borderRadius: "50px",
    padding: { xs: "4px 16px", sm: "5px 24px", md: "5px 30px" },
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "unset",
  }),
  pricingCard: {
    textTransform: "capitalize",
    flex: 1,
    mb: 1,
    padding: { xs: "3px", sm: "2px", md: "1px", lg: "1px", xl: "3px" },
    borderRadius: "10px",
    border: "0.2px solid #161717",
  },
  pricingTitle: {
    textTransform: "capitalize",
    fontSize: "20px",
    fontWeight: 600,
    margin: "0 auto",
    border: "0.2px solid #161717",
    maxWidth: "fit-content",
    padding: "1px 25px",
    borderRadius: "20px",
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "unset",
  },
  pricingPrice: {
    fontSize: "40px",
    fontWeight: 600,
    textAlign: "center",
  },
  pricingSubtitle: {
    textTransform: "capitalize",
    marginBottom: "5px",
    fontSize: "20px",
    fontWeight: 500,
    textAlign: "center",
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "unset",
  },
  featureText: {
    textTransform: "capitalize",
    fontSize: { xs: "15px", sm: "15px", md: "15px", lg: "15px", xl: "15px" },
    fontWeight: 400,
    textAlign: "start",
    display: "flex",
    gap: { xs: "9px", sm: "7px", md: "7px", lg: "4px", xl: "9px" },
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "unset",
  },
  featureIcon: {
    color: "#000",
  },
};

const Portfolio: PortfolioItem[] = [
  {
    message: "Sign Up & View Verified List of Contractors As Per Location",
    image: img,
  },
  {
    message: "Sign Up & View Verified List of Contractors As Per Location",
    image: img,
  },
  {
    message: "Sign Up & View Verified List of Contractors As Per Location",
    image: img,
  },
];

const services: string[] = [
  "Civil Construction",
  "Swimming",
  "Elevation",
  "Flooring/ Tiling",
  "Garden",
  // "Grills and Fabrication",
  "Ceiling Work",
  "Water Tank",
  "Waterproofing",
];